import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Pagination } from "../../../components/ui/Pagination";
import { MultiSelectFilter } from "../../../components/forms/MultiSelectFilter";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { damagesService } from "../../../services/damages";
import { formatApiError } from "../../../services/api";
import { DAMAGE_STATUS_TONE, SEVERITY_TONE } from "../../orders/lifecycle";
import { DamageFormModal } from "../components/DamageFormModal";
import { useAuth } from "../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../app/permissions";

const SEVERITIES = ["minor", "major", "total_loss"];
const STATUSES = ["open", "investigating", "charged", "resolved"];

export default function DamagesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // Admin retains full CRUD. Warehouse (A14) is view-only.
  const canManage = hasPermission(user, Permission.ADMIN_DAMAGES_MANAGE);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (search.trim()) params.search = search.trim();
      if (severityFilter.length) params.severity = severityFilter.join(",");
      if (statusFilter.length) params.status = statusFilter.join(",");
      const d = await damagesService.list(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (err) {
      setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, severityFilter, statusFilter, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Deep-link support: /damages?id=<uuid> opens the edit modal on load.
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || modalOpen) return;
    damagesService
      .get(id)
      .then((doc) => {
        setEditing(doc);
        setModalOpen(true);
        searchParams.delete("id");
        setSearchParams(searchParams, { replace: true });
      })
      .catch(() => {
        toast.error(t("admin.damages.not_found"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await damagesService.update(editing.id, payload);
        toast.success(t("admin.damages.saved"));
      } else {
        await damagesService.create(payload);
        toast.success(t("admin.damages.created"));
      }
      setModalOpen(false);
      setEditing(null);
      await reload();
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "PRODUCT_NOT_IN_ORDER")
        toast.error(t("admin.damages.err_product_not_in_order"));
      else toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await damagesService.remove(deleting.id);
      toast.success(t("admin.damages.deleted"));
      setDeleting(null);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setDeleteBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  const severityOptions = useMemo(
    () => SEVERITIES.map((s) => ({ value: s, label: t(`admin.damages.severity_${s}`) })),
    [t]
  );
  const statusOptions = useMemo(
    () => STATUSES.map((s) => ({ value: s, label: t(`admin.damages.status_${s}`) })),
    [t]
  );

  return (
    <>
      <div className="space-y-6" data-testid="damages-page">
        <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-champagne" aria-hidden />
              <h1
                className="text-3xl font-semibold tracking-tight text-text-primary"
                data-testid="damages-title"
              >
                {t("admin.damages.title")}
              </h1>
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              {t("admin.damages.subtitle")}
            </p>
          </div>
          <Button
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            data-testid="damages-add-new"
            disabled={!canManage}
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne disabled:hidden"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("admin.damages.add_new_cta")}
          </Button>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t("admin.damages.search_placeholder")}
              data-testid="damages-search"
              className="h-10 pl-9 bg-surface border-border-subtle focus-visible:ring-champagne"
            />
          </div>
          <MultiSelectFilter
            selected={severityFilter}
            options={severityOptions}
            onChange={(arr) => {
              setSeverityFilter(arr);
              setPage(1);
            }}
            triggerLabel={t("admin.damages.filter_severity")}
            testIdRoot="damages-severity-filter"
          />
          <MultiSelectFilter
            selected={statusFilter}
            options={statusOptions}
            onChange={(arr) => {
              setStatusFilter(arr);
              setPage(1);
            }}
            triggerLabel={t("admin.damages.filter_status")}
            testIdRoot="damages-status-filter"
          />
        </div>

        {error ? (
          <ErrorState
            title={t("common.error_generic")}
            body={error}
            onRetry={reload}
            testId="damages-error"
          />
        ) : loading ? (
          <div className="space-y-2" data-testid="damages-loading">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("admin.damages.empty_title")}
            body={t("admin.damages.empty_body")}
            testId="damages-empty"
          />
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface shadow-card">
              <table className="min-w-full text-sm">
                <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_number")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_reported_by")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_brand")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_order")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_delivery_address")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_description")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_severity")}</th>
                    <th className="px-4 py-3 font-semibold">{t("admin.damages.col_status")}</th>
                    {canManage && (
                      <th className="px-4 py-3 text-right font-semibold">{t("common.actions")}</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {items.map((d) => (
                    <tr
                      key={d.id}
                      data-testid={`damage-row-${d.id}`}
                      className="hover:bg-offwhite/50"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-text-primary">
                        {d.damage_number}
                      </td>
                      <td className="px-4 py-3 text-text-primary">
                        {d.reported_by_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-text-primary">{d.brand || "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/orders/${d.order_id}`)}
                          data-testid={`damage-order-link-${d.id}`}
                          className="text-left"
                        >
                          <div className="font-mono text-xs text-text-primary">
                            {d.order_number ? `#${d.order_number}` : "—"}
                          </div>
                          <div className="max-w-[12rem] truncate text-xs text-text-secondary">
                            {d.order?.event_name || ""}
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-text-secondary text-xs">
                        {d.order?.delivery_address ? (
                          <>
                            <div className="max-w-[10rem] truncate">
                              {d.order.delivery_address.street}
                            </div>
                            <div>
                              {d.order.delivery_address.postal_code}{" "}
                              {d.order.delivery_address.city}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-primary">
                        <div className="max-w-[16rem] truncate">{d.description}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${SEVERITY_TONE[d.severity] || SEVERITY_TONE.minor}`}
                          data-testid={`damage-severity-badge-${d.id}`}
                        >
                          {t(`admin.damages.severity_${d.severity}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${DAMAGE_STATUS_TONE[d.status] || DAMAGE_STATUS_TONE.open}`}
                          data-testid={`damage-status-badge-${d.id}`}
                        >
                          {t(`admin.damages.status_${d.status}`)}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setEditing(d);
                                setModalOpen(true);
                              }}
                              data-testid={`damage-edit-${d.id}`}
                              aria-label={t("common.edit")}
                              title={t("common.edit")}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-primary transition-colors hover:bg-offwhite"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleting(d)}
                              data-testid={`damage-delete-${d.id}`}
                              aria-label={t("common.delete")}
                              title={t("common.delete")}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-status-unavailable transition-colors hover:bg-status-unavailable/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              total={total}
              testId="damages-pagination"
            />
          </>
        )}
      </div>

      <DamageFormModal
        open={modalOpen}
        onOpenChange={(v) => (v ? null : (setModalOpen(false), setEditing(null)))}
        initial={editing}
        onSubmit={submit}
        submitting={saving}
      />

      <ConfirmDeleteModal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        name={deleting?.damage_number || ""}
        busy={deleteBusy}
        testId="damages-delete"
      />
    </>
  );
}
